import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, Upload, Trash2 } from 'lucide-react';
import CameraCapture from './CameraCapture';
import { useImageViewer } from './ImageViewerProvider';
import { 
  uploadWorkflowPhoto, 
  getWorkflowStepPhotos, 
  deleteWorkflowPhoto,
  getWorkflowPhotoUrl,
  blobUrlToFile,
  type WorkflowPhoto 
} from '../services/workflowPhotoService';

interface WorkflowPhotoCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  stepTitle: string;
  stepId: string;
  bookingId: string;
  serviceId: string;
  onPhotosUpdated?: () => void;
}

export default function WorkflowPhotoCapture({
  isOpen,
  onClose,
  stepTitle,
  stepId,
  bookingId,
  serviceId,
  onPhotosUpdated
}: WorkflowPhotoCaptureProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [photos, setPhotos] = useState<WorkflowPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState<string[]>([]); // Track which photos are being uploaded
  const { open: openImageViewer } = useImageViewer();
  const fileInputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    console.log('📸 WorkflowPhotoCapture isOpen changed:', isOpen);
    if (isOpen) {
      console.log('📸 Modal opening, setting animation to true');
      setIsAnimating(true);
      loadExistingPhotos();
    } else {
      // Reset state when modal closes
      setPhotos([]);
      setUploadingPhotos([]);
    }
  }, [isOpen, bookingId, stepId]);

  const loadExistingPhotos = async () => {
    try {
      console.log('📸 Loading existing photos for step:', stepId);
      const existingPhotos = await getWorkflowStepPhotos(bookingId, stepId);
      setPhotos(existingPhotos);
      console.log('📸 Loaded existing photos:', existingPhotos.length);
    } catch (error) {
      console.error('❌ Error loading existing photos:', error);
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 150);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    console.log('📸 Processing file uploads:', files.length);
    setUploading(true);

    try {
      // Upload each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        try {
          const result = await uploadWorkflowPhoto({
            bookingId,
            workflowStepId: stepId,
            serviceId,
            file
          });

          if (result.success && result.photo) {
            setPhotos(prev => [...prev, result.photo!]);
            console.log('✅ File uploaded successfully:', result.photo.id);
          } else {
            console.error('❌ File upload failed:', result.error);
            alert(`Failed to upload ${file.name}: ${result.error}`);
          }
        } catch (error) {
          console.error('❌ Error uploading file:', error);
          alert(`Failed to upload ${file.name}`);
        }
      }
      
      if (onPhotosUpdated) {
        onPhotosUpdated();
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCameraCapture = () => {
    setShowCamera(true);
  };

  const handleCameraPhotos = async (newPhotos: string[]) => {
    setShowCamera(false);
    
    if (newPhotos.length === 0) return;

    console.log('📸 Processing camera photos:', newPhotos.length);
    setUploading(true);

    try {
      // Upload each photo
      for (let i = 0; i < newPhotos.length; i++) {
        const blobUrl = newPhotos[i];
        const fileName = `camera-photo-${Date.now()}-${i + 1}.jpg`;
        
        // Track this photo as uploading
        setUploadingPhotos(prev => [...prev, blobUrl]);
        
        try {
          // Convert blob URL to File
          const file = await blobUrlToFile(blobUrl, fileName);
          
          // Upload to Supabase
          const result = await uploadWorkflowPhoto({
            bookingId,
            workflowStepId: stepId,
            serviceId,
            file
          });

          if (result.success && result.photo) {
            // Add to photos list
            setPhotos(prev => [...prev, result.photo!]);
            console.log('✅ Photo uploaded successfully:', result.photo.id);
          } else {
            console.error('❌ Photo upload failed:', result.error);
            alert(`Failed to upload photo: ${result.error}`);
          }
        } catch (error) {
          console.error('❌ Error processing photo:', error);
          alert('Failed to process photo');
        } finally {
          // Remove from uploading list
          setUploadingPhotos(prev => prev.filter(url => url !== blobUrl));
          // Clean up blob URL
          URL.revokeObjectURL(blobUrl);
        }
      }

      if (onPhotosUpdated) {
        onPhotosUpdated();
      }
    } finally {
      setUploading(false);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
  };

  const handleDeletePhoto = async (index: number) => {
    const photo = photos[index];
    if (!photo) return;

    console.log('📸 Deleting photo:', photo.id);
    
    try {
      const success = await deleteWorkflowPhoto(photo.id);
      if (success) {
        setPhotos(prev => prev.filter((_, i) => i !== index));
        console.log('✅ Photo deleted successfully');
        
        if (onPhotosUpdated) {
          onPhotosUpdated();
        }
      } else {
        alert('Failed to delete photo. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error deleting photo:', error);
      alert('Failed to delete photo. Please try again.');
    }
  };

  if (!isOpen) {
    console.log('📸 Modal not open, returning null');
    return null;
  }

  console.log('📸 Rendering modal content with portal to document.body');
  const modalContent = (
    <div className="fixed inset-0 z-[99999] overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className={`fixed inset-0 bg-gray-500 transition-opacity duration-150 ${
            isAnimating ? 'bg-opacity-75' : 'bg-opacity-0'
          }`}
          onClick={handleClose}
        />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div
          className={`inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all duration-150 w-full max-w-[95vw] sm:my-8 sm:align-middle sm:max-w-4xl ${
            isAnimating ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'
          }`}
          style={{ maxHeight: '90vh' }}
        >
          {/* Header */}
          <div className="px-4 pt-5 pb-4 sm:p-6 bg-gradient-to-br from-blue-50 to-purple-50 border-b border-blue-200/30">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <Camera className="h-6 w-6 mr-2 text-blue-600" />
                  Photo Capture
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Capture photos for "{stepTitle}"
                </p>
              </div>
              <button
                onClick={handleClose}
                className="rounded-xl text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 p-2 hover:bg-gray-100/80 transition-all duration-200"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            {/* Photo Capture Buttons */}
            <div className="mb-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCameraCapture}
                disabled={uploading || showCamera}
                className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <Camera className="h-5 w-5 mr-2" />
                {showCamera ? 'Camera Open...' : 'Open Camera'}
              </button>
              
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex-1 flex items-center justify-center px-4 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                <Upload className="h-5 w-5 mr-2" />
                Upload Photos
              </button>
            </div>

            {/* Photo Grid */}
            {photos.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {photos.map((photo, index) => (
                  <div key={photo.id} className="relative group">
                    <img
                      src={getWorkflowPhotoUrl(photo.file_path)}
                      alt={photo.file_name}
                      onClick={() => openImageViewer({
                        images: photos.map((ph) => ({ src: getWorkflowPhotoUrl(ph.file_path), caption: ph.file_name })),
                        index,
                      })}
                      className="w-full h-32 object-cover rounded-lg border border-gray-200 cursor-pointer"
                    />
                    <button
                      onClick={() => handleDeletePhoto(index)}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-sm">No photos captured yet</p>
                <p className="text-gray-400 text-xs mt-1">Click "Take Photo" or "Upload Photos" to get started</p>
              </div>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="w-full inline-flex justify-center rounded-2xl border border-transparent shadow-sm px-6 py-3 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm transition-all duration-200"
            >
              Done ({photos.length} photo{photos.length !== 1 ? 's' : ''})
            </button>
          </div>
        </div>
      </div>

      {/* Camera Capture Modal */}
      <CameraCapture
        isOpen={showCamera}
        onClose={handleCameraClose}
        onPhotosCapture={handleCameraPhotos}
        title={`Photos for "${stepTitle}"`}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}
